import Vue from 'vue';
import Router from 'vue-router';
import Login from '../pages/Login.vue';
import store from '@/store';
import { VENUE_STORAGE, USER_ROLES } from '@/constants';
import { getSelectedVenue } from '@/services/helpers';
import { USER_CUSTOMIZE_MENUS } from '../constants';
import Actions from '../pages/Actions.vue';

const GameAdmin = () => import('../pages/GameAdmin.vue');
const AppBuilder = () => import('../pages/AppBuilder.vue');
const BannerConfig = () => import('../pages/BannerConfig.vue');
const Devices = () => import('../pages/Devices.vue');
const Device = () => import('../pages/Device.vue');
const Schedules = () => import('../pages/Schedules.vue');
const AddSchedule = () => import('../pages/AddSchedule.vue');
const EditSchedule = () => import('../pages/EditSchedule.vue');
const Playlists = () => import('../pages/Playlists.vue');
const AddPlaylist = () => import('../pages/AddPlaylist.vue');
const EditPlaylist = () => import('../pages/EditPlaylist.vue');
const Groups = () => import('../pages/Groups.vue');
const GroupDevices = () => import('../pages/GroupDevices.vue');
const Assets = () => import('../pages/Assets.vue');
const AddAsset = () => import('../pages/AddAsset.vue');
const AddAssetMultiple = () => import('../pages/AddAssetMultiple.vue');
const EditAsset = () => import('../pages/EditAsset.vue');
const Users = () => import('../pages/Users.vue');
const UserProfile = () => import('../pages/UserProfile.vue');
const Settings = () => import('../pages/Settings.vue');
const Venues = () => import('../pages/Venues.vue');
const MFASetup = () => import('../pages/MFASetup.vue');
const Authenticator = () => import('../pages/Authenticator.vue');
const NoPermission = () => import('../pages/NoPermission.vue');
const PromotionManagement = () => import('../pages/PromotionManagement.vue');
const PromotionDetail = () => import('../pages/PromotionDetail.vue');
const M5UI = () => import('../pages/M5UI.vue');
const ForgotPassword = () => import('../pages/ForgotPassword.vue');
const VerifyResetCode = () => import('../pages/VerifyResetCode.vue');
const ResetPassword = () => import('../pages/ResetPassword.vue');

Vue.use(Router);

const originalPush = Router.prototype.push;
Router.prototype.push = function push(location) {
  return originalPush.call(this, location).catch(err => {
    if (
      err.name !== 'NavigationDuplicated' &&
      !err.message.includes('Navigation aborted') &&
      !err.message.includes('Redirected when going from')
    ) {
      throw err;
    }
  });
};

const originalReplace = Router.prototype.replace;
Router.prototype.replace = function replace(location) {
  return originalReplace.call(this, location).catch(err => {
    if (
      err.name !== 'NavigationDuplicated' &&
      !err.message.includes('Navigation aborted') &&
      !err.message.includes('Redirected when going from')
    ) {
      throw err;
    }
  });
};

let router = new Router({
  routes: [
    {
      path: '*',
      name: 'Error',
      meta: {
        layout: 'error' // name of the layout
      }
    },
    {
      path: '/',
      name: 'Login',
      component: Login,
      meta: {
        layout: 'login',
        requiresGuest: true
      }
    },
    {
      path: '/callback',
      name: 'OktaCallback',
      component: Login,
      meta: {
        layout: 'login',
        isOktaCallback: true
      }
    },
    {
      path: '/forgot-password',
      name: 'ForgotPassword',
      component: ForgotPassword,
      meta: {
        layout: 'login',
        requiresGuest: true
      }
    },
    {
      path: '/verify-reset-code',
      name: 'VerifyResetCode',
      component: VerifyResetCode,
      meta: {
        layout: 'login',
        requiresGuest: true
      }
    },
    {
      path: '/reset-password',
      name: 'ResetPassword',
      component: ResetPassword,
      meta: {
        layout: 'login',
        requiresGuest: true
      }
    },
    {
      path: '/mfa-setup',
      name: 'MFA Setup',
      component: MFASetup,
      meta: {
        requiresGuest: true,
        layout: 'mfa'
      }
    },
    {
      path: '/otp-authenticate',
      name: 'OTP Authenticator',
      component: Authenticator,
      meta: {
        requiresGuest: true,
        layout: 'login'
      }
    },
    {
      path: '/:venueId/devices',
      name: 'Devices',
      component: Devices,
      meta: {
        requiresAuth: true
      }
    },
    {
      path: '/:venueId/devices/:_id',
      name: 'Device',
      component: Device,
      meta: {
        requiresAuth: true
      }
    },
    {
      path: '/:venueId/groups',
      name: 'Groups',
      component: Groups,
      meta: {
        requiresAuth: true
      }
    },
    {
      path: '/groupdevices',
      name: 'GroupDevices',
      component: GroupDevices,
      meta: {
        layout: 'dashboard'
      }
    },
    {
      path: '/:venueId/assets',
      name: 'Assets',
      component: Assets,
      meta: {
        requiresAuth: true
      }
    },
    {
      path: '/:venueId/assets/new',
      name: 'AddAsset',
      component: AddAsset,
      meta: {
        requiresAuth: true
      }
    },
    {
      path: '/:venueId/assets/multinew',
      name: 'AddAssetMultiple',
      component: AddAssetMultiple,
      meta: {
        requiresAuth: true
      }
    },
    {
      path: '/:venueId/assets/edit/:_id',
      name: 'EditAsset',
      component: EditAsset,
      meta: {
        requiresAuth: true
      }
    },
    {
      path: '/:venueId/assets_folders',
      name: 'AssetsFolders',
      meta: {
        requiresAuth: true
      }
    },
    {
      path: '/:venueId/playlists',
      name: 'Playlists',
      component: Playlists,
      meta: {
        requiresAuth: true
      }
    },
    {
      path: '/:venueId/playlists/new',
      name: 'AddPlaylist',
      component: AddPlaylist,
      meta: {
        requiresAuth: true
      }
    },
    {
      path: '/:venueId/playlists/edit/:id',
      name: 'EditPlaylist',
      component: EditPlaylist,
      meta: {
        requiresAuth: true
      }
    },
    {
      path: '/:venueId/schedules',
      name: 'Schedules',
      component: Schedules,
      meta: {
        requiresAuth: true
      }
    },
    {
      path: '/:venueId/gameadmin',
      name: 'Game Admin',
      component: GameAdmin,
      meta: {
        requiresAuth: true
      }
    },
    {
      path: '/:venueId/actions',
      name: 'Actions',
      component: Actions,
      meta: {
        requiresAuth: true
      }
    },
    {
      path: '/:venueId/appbuilder',
      name: 'App Builder',
      component: AppBuilder,
      meta: {
        requiresAuth: true
      }
    },
    {
      path: '/:venueId/schedules/new',
      name: 'AddSchedule',
      component: AddSchedule,
      meta: {
        requiresAuth: true
      }
    },
    {
      path: '/:venueId/schedules/edit/:id',
      name: 'EditSchedule',
      component: EditSchedule,
      meta: {
        requiresAuth: true
      }
    },
    {
      path: '/:venueId/promotion-management',
      name: 'Promotion Management',
      component: PromotionManagement,
      meta: {
        requiresAuth: true
      }
    },
    {
      path: '/:venueId/promotion-management/detail/:id?',
      name: 'PromotionDetail',
      component: PromotionDetail,
      meta: {
        requiresAuth: true
      }
    },
    {
      path: '/:venueId/player-portal',
      name: 'Player Portal',
      component: M5UI,
      meta: {
        requiresAuth: true
      }
    },
    {
      path: '/users',
      name: 'Users',
      component: Users,
      meta: {
        requiresAuth: true
      }
    },
    {
      path: '/userprofile',
      name: 'User Profile',
      component: UserProfile,
      meta: {
        requiresAuth: true
      }
    },
    {
      path: '/settings',
      name: 'Settings',
      component: Settings,
      meta: {
        requiresAuth: true
      }
    },
    {
      path: '/venues',
      name: 'Venues',
      component: Venues,
      meta: {
        requiresAuth: true
      }
    },
    {
      path: '/no-permission',
      name: 'No permission',
      component: NoPermission,
      meta: {
        requiresAuth: false
      }
    }
  ]
});

/* AUTH GUARD */
router.beforeEach(async (to, _, next) => {
  // Skip all guards for Okta callback route
  if (to.meta.isOktaCallback) {
    return next();
  }

  const { isAuthenticated, userVenues } = store.state.users;
  const { rootUser, role, menus } = store.state.users.user;
  const { license } = store.state;
  let venueId;

  if (to.path === 'mfa-setup' || to.path === 'otp-authenticate') {
    return next();
  }

  if (!rootUser && isAuthenticated) {
    const selected = getSelectedVenue();

    if (!selected) {
      if (userVenues.length) {
        venueId = userVenues[0]._id;
        sessionStorage.setItem(VENUE_STORAGE, JSON.stringify(userVenues[0]));
      } else {
        store.dispatch('users/LOGOUT');
      }
    } else {
      const exists = userVenues.find(i => i._id === selected._id);

      if (exists) {
        venueId = selected._id;
        sessionStorage.setItem(VENUE_STORAGE, JSON.stringify(exists));
      } else if (!exists && userVenues[0]) {
        sessionStorage.setItem(VENUE_STORAGE, JSON.stringify(userVenues[0]));
        venueId = userVenues[0]._id;
      }

      venueId = selected._id;
    }
  }

  if (to.matched.some(record => record.meta.requiresAuth)) {
    if (!isAuthenticated) {
      return next({
        path: '/'
      });
    } else {
      // If license error, redirect to settings page
      if (to.path !== '/settings' && license.hasError) {
        return next({
          path: '/settings'
        });
      }

      // if page is not memberdraw and role is memberdraw_staff, redirect it back to memberdraw
      if (role === USER_ROLES.MEMBERDRAW_STAFF) {
        window.location.href = `${window.location.origin}/${venueId}/member-draw`;
        return;
      }

      // If page is settings and role is not admin or not root user, redirect to devices
      if (to.path === '/settings') {
        if (rootUser || role === USER_ROLES.ADMINISTRATOR || role === USER_ROLES.SUPER_USER) {
          return next();
        } else {
          return next({
            path: `/${venueId}/devices`
          });
        }
      }

      const specialPages = ['/users', '/venues'];

      if (
        specialPages.includes(to.path) &&
        (rootUser ||
          role === USER_ROLES.ADMINISTRATOR ||
          role === USER_ROLES.SUPER_USER ||
          role == USER_ROLES.REGULAR_USER)
      ) {
        return next();
      } else if (
        specialPages.includes(to.path) &&
        (!rootUser || role === USER_ROLES.REGULAR_USER)
      ) {
        return next({
          path: `/${venueId}/devices`
        });
      } else {
        if (rootUser) {
          return next({
            path: '/users'
          });
        } else {
          if (role === USER_ROLES.NONPUBLISH_USER) {
            const permissions = await store.dispatch('users/getNPUserSettings');
            if (!permissions.pages.includes(to.name)) {
              return next({ name: 'No permission' });
            }
          }
          return next();
        }
      }
    }
  } else if (to.matched.some(record => record.meta.requiresGuest)) {
    if (isAuthenticated) {
      if (license.hasError) {
        return next({
          path: '/settings'
        });
      } else {
        return next({
          path:
            rootUser || role === USER_ROLES.REGULAR_USER ? '/userprofile' : `/${venueId}/devices`
        });
      }
    } else {
      return next();
    }
  } else {
    return next();
  }
});

export default router;



// WEBPACK FOOTER //
// ./src/router/index.js